import { IPropertyDetailsResponse } from "@/src-refactor/modules/store/qrtk/services/realestate/responses/property-details.response";
import MoneyFormat from "@/src-refactor/modules/shared/helpers/moneyFormat.helper";
import { getConditionStatusLabel } from "@/src-refactor/modules/shared/components/refactor/helpers/property/prepare-condition-status-label";
import type {
  PropertyBrochureData,
  PropertyBrochureFeature
} from "../hooks/usePropertyBrochurePdf";
import { prepareDetailPropertyDescription } from "./detail/prepareDetailPropertyDescription";
import { prepareDetailPropertyFeatures } from "./detail/prepareDetailPropertyFeatures";
import { prepareDetailPropertyImages } from "./detail/prepareDetailPropertyImages";
import { prepareDetailPropertyName } from "./detail/prepareDetailPropertyName";
import { prepareDetailPropertyPosition } from "./detail/prepareDetailPropertyPosition";
import { prepareDetailPropertyPrice } from "./detail/prepareDetailPropertyPrice";
import { prepareDetailPropertyVisit } from "./detail/prepareDetailPropertyVisit";
import prepareImprovements from "@rf-helper/property/prepare-improments";
import ENVIRONMENT_VAR from "@/src-refactor/modules/shared/model/const/env/env.const";

type FeatureKey = keyof ReturnType<typeof prepareDetailPropertyFeatures>;

const formatArea = (value: string | number | null | undefined): string | null => {
  if (value === null || typeof value === "undefined") {
    return null;
  }

  const raw = typeof value === "number" ? value.toString() : value;
  if (!raw) {
    return null;
  }

  const formatted = MoneyFormat(raw);
  return formatted ? `${formatted} m²` : null;
};

const formatNumeric = (value: number | string | null | undefined): string | null => {
  if (value === null || typeof value === "undefined") {
    return null;
  }

  if (typeof value === "number") {
    return value.toString();
  }

  return value.trim() ? value : null;
};

const FEATURE_CONFIG: Array<{
  key: FeatureKey;
  label: string;
  icon: string;
  formatter?: (value: number | string | null | undefined) => string | null;
}> = [
  { key: "bedrooms", label: "Habitaciones", icon: "bed" },
  { key: "bathrooms", label: "Baños", icon: "bath" },
  { key: "halfBathrooms", label: "Medios baños", icon: "half-bath" },
  { key: "parkingSpots", label: "Parqueos", icon: "parking" },
  { key: "floorLevel", label: "Nivel", icon: "level" },
  { key: "floorsTotal", label: "Pisos", icon: "floors" },
  { key: "sqmLand", label: "Terreno", icon: "land", formatter: formatArea },
  {
    key: "sqmConstruction",
    label: "Construcción",
    icon: "construction",
    formatter: formatArea
  },
  { key: "pumps", label: "Bombas", icon: "pump" }
];

const buildFeatureList = (
  data: IPropertyDetailsResponse
): PropertyBrochureFeature[] => {
  const features = prepareDetailPropertyFeatures(data);
  if (!features) {
    return [];
  }

  return FEATURE_CONFIG.reduce<PropertyBrochureFeature[]>((acc, featureConfig) => {
    const rawValue = features[featureConfig.key];
    const formattedValue = featureConfig.formatter
      ? featureConfig.formatter(rawValue)
      : formatNumeric(rawValue);

    if (formattedValue) {
      acc.push({
        label: featureConfig.label,
        value: formattedValue,
        icon: featureConfig.icon
      });
    }

    return acc;
  }, []);
};

const formatPropertyTypeForUrl = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\//g, "-o-")
    .replace(/\s+/g, "-")
    .replace(/-o--+/g, "-o-")
    .replace(/--o-+/g, "-o-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .replace(/-+/g, "-")
    .toLowerCase();
};

const buildPublicProjectUrl = (data: IPropertyDetailsResponse): string => {
  const domain = ENVIRONMENT_VAR.REMAX_DOMAIN;
  const propertyId = data?.id;

  if (!domain || !propertyId) {
    return "";
  }

  const propertyType = data?.projecttype || data?.realstate_type || "propiedad";
  const formattedType = formatPropertyTypeForUrl(propertyType) || "propiedad";

  return `${domain}/propiedad/${formattedType}/${propertyId}`;
};

const propertyBrochureAdapter = (
  data: IPropertyDetailsResponse
): PropertyBrochureData => {
  const description = prepareDetailPropertyDescription(data);
  const {
    use,
    position,
    features,
    characteristicsCabinets,
    characteristicsClosets,
    characteristicsDoors,
    characteristicsFloors,
    characteristicsFrames,
    characteristicsRoof,
    characteristicsRoofForm,
    characteristicsTypeHouse,
    characteristicsWindows
  } = prepareImprovements(data);

  return {
    id: data?.id ?? "",
    projectUrl: buildPublicProjectUrl(data),
    images: prepareDetailPropertyImages(data) ?? [],
    title: prepareDetailPropertyName(data) ?? "",
    nameProperty: data?.name ?? "",
    sector: data?.sector ?? "",
    city: data?.city ?? "",
    prices: prepareDetailPropertyPrice(data),
    description: description?.rawEs || "",
    characteristics: [
      ...characteristicsCabinets,
      ...characteristicsFloors,
      ...characteristicsWindows,
      ...characteristicsClosets,
      ...characteristicsDoors,
      ...characteristicsFrames,
      ...characteristicsRoof,
      ...characteristicsRoofForm,
      ...characteristicsTypeHouse
    ],
    conditionStatus:
      getConditionStatusLabel(data?.condition_status, data?.business_type) ?? "",
    position: prepareDetailPropertyPosition(data) ?? "",
    uses: use.map(item => item.label).join(", "),
    visit: prepareDetailPropertyVisit(data) ?? "",
    feature: buildFeatureList(data)
  };
};

export default propertyBrochureAdapter;
